import { useEffect, useRef } from "react";
import { useGoogleMaps } from "../utils/googleMaps";

/* A place field with Google's suggestions. Google's own Places widget on a plain input
   (27 Oct 2026) - the React wrapper library is gone from every page, and Maps loads on
   demand (utils/googleMaps): until it is ready the field still takes typing. The value
   handed to onChange is the same as before. */
const DEFAULT_OPTIONS = {
  types: ["geocode"],                      // City / area / street level
  componentRestrictions: { country: "in" } // India
};

export default function LocationAutocomplete({
  value,
  onChange,
  onBlur,
  placeholder,
  className = "",
  options,
  hasError
}) {
  const inputRef = useRef(null);
  const ready = useGoogleMaps();
  // The latest onChange, for a listener added once.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const acRef = useRef(null);

  useEffect(() => {
    if (!ready || !inputRef.current) return undefined;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, options || DEFAULT_OPTIONS);
    acRef.current = ac;
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const address = place?.formatted_address;
      const geometry = place?.geometry?.location;
      if (!address) return;
      onChangeRef.current({
        value: address,
        selected: true,
        placeId: place?.place_id,
        location: geometry ? { lat: geometry.lat(), lng: geometry.lng() } : null,
        // Google already knows a locality's parent city; this is where it says so.
        addressComponents: place?.address_components || []
      });
    });
    return () => {
      listener.remove();
      window.google.maps.event.clearInstanceListeners(ac);
      acRef.current = null;
    };
    // The widget is made once; its options follow below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // A picker narrows the suggestions once the city is known (bounds) - as the wrapper did.
  useEffect(() => {
    if (acRef.current) acRef.current.setOptions(options || DEFAULT_OPTIONS);
  }, [options]);

  return (
    <input
      ref={inputRef}
      type="text"
      className={`input ${className} ${hasError ? '!border-[#DC3545]' : '!border-[#EEF2F7]'}`}
      placeholder={placeholder}
      value={value.value || ""}
      onChange={(e) =>
        onChange({
          value: e.target.value,
          selected: false
        })
      }
      onBlur={onBlur}
    />
  );
}
